<template>
    <div class="space-y-4">

        <!-- ── Docs techniques ─────────────────────────── -->
        <template v-if="technicalDocs.length > 0">
            <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Documentation technique</p>
            <div class="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
                <a
                    v-for="doc in technicalDocs"
                    :key="doc.id"
                    :href="doc.easysav_doc_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="shrink-0 w-36 bg-white rounded-2xl border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow"
                >
                    <div class="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-2">
                        <svg class="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                        </svg>
                    </div>
                    <p class="text-xs font-semibold text-slate-700 line-clamp-2 mb-2">{{ doc.type }}</p>
                    <div class="flex items-center gap-1 text-blue-600">
                        <span class="text-xs font-semibold">Ouvrir</span>
                        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17L17 7M7 7h10v10" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </div>
                </a>
            </div>
        </template>

        <!-- ── Support fabricant ────────────────────────── -->
        <template v-if="fournisseurTel">
            <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Support fabricant</p>
            <a
                :href="`tel:${fournisseurTel.tel.replace(/\s/g, '')}`"
                class="flex items-center justify-between bg-white rounded-2xl border border-gray-200 px-4 py-3 shadow-sm"
            >
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                        <svg class="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                    </div>
                    <div>
                        <p class="text-sm font-semibold text-slate-800">{{ fournisseurTel.marque }}</p>
                        <p class="text-xs text-slate-500">{{ fournisseurTel.tel }}</p>
                    </div>
                </div>
                <span class="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg">Appeler</span>
            </a>
        </template>

        <!-- ── Documents à générer ──────────────────────── -->
        <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Documents à générer</p>
        <div class="space-y-4">
            <div
                v-for="doc in docOptions"
                :key="doc.key"
                class="flex items-center gap-3 bg-white rounded-2xl border px-4 py-6 cursor-pointer transition-colors"
                :class="docsToGenerate[doc.key] ? 'border-orange-300 bg-orange-50/50' : 'border-gray-200'"
                @click="!doc.mandatory && toggleDoc(doc.key)"
            >
                <div
                    class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    :class="docsToGenerate[doc.key] ? 'bg-orange-100' : 'bg-gray-100'"
                >
                    <component :is="doc.icon" :active="docsToGenerate[doc.key]" />
                </div>
                <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold" :class="docsToGenerate[doc.key] ? 'text-orange-800' : 'text-slate-700'">{{ doc.title }}</p>
                    <p class="text-xs text-slate-400">{{ doc.sub }}</p>
                </div>
                <!-- Obligatoire -->
                <span v-if="doc.mandatory" class="text-[10px] font-bold text-white bg-orange-700 px-2 py-0.5 rounded-full">INCLUS</span>
                <!-- Toggle -->
                <div v-else
                    class="w-11 h-6 rounded-full transition-colors relative shrink-0"
                    :class="docsToGenerate[doc.key] ? 'bg-orange-500' : 'bg-gray-200'"
                >
                    <div
                        class="w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-all"
                        :class="docsToGenerate[doc.key] ? 'left-5' : 'left-0.5'"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const iconDoc   = { functional: true, render: (h, ctx) => h('svg', { attrs: { viewBox:'0 0 24 24', fill: ctx.props.active ? '#9A3412' : '#6b7280', class:'w-5 h-5' } }, [h('path', { attrs: { d:'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z' } })]) };
const iconSnow  = { functional: true, render: (h, ctx) => h('svg', { attrs: { viewBox:'0 0 24 24', fill:'none', stroke: ctx.props.active ? '#9A3412' : '#6b7280', 'stroke-width':'2', class:'w-5 h-5' } }, [h('line',{attrs:{x1:'12',y1:'2',x2:'12',y2:'22'}}),h('path',{attrs:{d:'M17 7l-5 5-5-5'}}),h('path',{attrs:{d:'M17 17l-5-5-5 5'}}),h('line',{attrs:{x1:'2',y1:'12',x2:'22',y2:'12'}}),h('path',{attrs:{d:'M7 7l5 5 5-5'}}),h('path',{attrs:{d:'M7 17l5-5 5 5'}})]) };
const iconLoop  = { functional: true, render: (h, ctx) => h('svg', { attrs: { viewBox:'0 0 24 24', fill:'none', stroke: ctx.props.active ? '#9A3412' : '#6b7280', 'stroke-width':'2', class:'w-5 h-5' } }, [h('path',{attrs:{d:'M23 4v6h-6'}}),h('path',{attrs:{d:'M1 20v-6h6'}}),h('path',{attrs:{d:'M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15'}})]) };
const iconCheck = { functional: true, render: (h, ctx) => h('svg', { attrs: { viewBox:'0 0 24 24', fill:'none', stroke: ctx.props.active ? '#9A3412' : '#6b7280', 'stroke-width':'2', class:'w-5 h-5' } }, [h('path',{attrs:{d:'M9 12l2 2 4-4'}}),h('circle',{attrs:{cx:'12',cy:'12',r:'9'}})]) };
const iconPv    = { functional: true, render: (h, ctx) => h('svg', { attrs: { viewBox:'0 0 24 24', fill:'none', stroke: ctx.props.active ? '#9A3412' : '#6b7280', 'stroke-width':'2', class:'w-5 h-5' } }, [h('path',{attrs:{d:'M12 20h9'}}),h('path',{attrs:{d:'M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z'}})]) };

export default {
    name: 'StepDocumentation',
    inject: ['wm'],

    computed: {
        technicalDocs() { return this.wm.intervention.docs ?? []; },
        fournisseurTel() { return this.wm.intervention.appareil?.fournisseur_tel ?? null; },
        docsToGenerate() { return this.wm.docsToGenerate; },
        isClim()         { return this.wm.isClim; },
        isEntretien()    { return this.wm.isEntretien; },
        isInstallation() { console.log(this.wm.isInstallation); return this.wm.isInstallation; },

        docOptions() {
            return [
                { key: 'bon_intervention', title: "Bon d'intervention", sub: 'Récapitulatif des travaux', mandatory: true, icon: iconDoc },
                ...(this.isClim ? [
                    { key: 'cerfa', title: 'CERFA', sub: 'Fluides frigorigènes', icon: iconSnow }
                ] : []),
                ...(this.isEntretien ? [
                    { key: 'attestation_entretien', title: "Attestation d'entretien", sub: 'Prestation réalisée', icon: iconCheck },
                    { key: 'contrat_entretien', title: "Contrat d'entretien", sub: 'Maintenance annuelle', icon: iconLoop },
                ] : []),
                ...(this.isInstallation ? [
                    { key: 'attestation_entretien', title: "Attestation d'entretien", sub: 'Points de contrôle', icon: iconCheck },
                    { key: 'pv_reception', title: 'PV de réception', sub: 'Procès-verbal de réception', icon: iconPv },
                ] : []),
            ];
        },
    },

    methods: {
        toggleDoc(key) {
            this.wm.docsToGenerate = { ...this.wm.docsToGenerate, [key]: !this.wm.docsToGenerate[key] };
        },
    },
};
</script>
